import { ErrorMessage, Field, useFormikContext } from "formik";
import LabelWithTooltip from "@app/components/forms/labelWithTooltip";
import { useEffect } from "react";

interface Option {
  value: string;
  label: string;
}

interface OptionGroup {
  label: string;
  options: Option[];
}

type SelectOptions = Option[] | OptionGroup[];

interface SelectFieldProps {
  label: string;
  id: string;
  name: string;
  options: SelectOptions;
  required?: boolean;
  multiple?: boolean;
  tooltipText?: string;
  tooltipLink?: string;
}

function isOptionGroup(option: Option | OptionGroup): option is OptionGroup {
  return "options" in option;
}

export function SelectField({
  label,
  id,
  name,
  required,
  multiple = false,
  options,
  tooltipText,
  tooltipLink,
}: SelectFieldProps) {
  const { setFieldValue, values } = useFormikContext<Record<string, string>>();

  // Auto-select first item
  const currentValue = values[name];
  useEffect(() => {
    if (options.length > 0 && !currentValue && !multiple) {
      // Check if the first item is an option group or a single option
      const firstOption = options[0]!;
      const firstValue = isOptionGroup(firstOption)
        ? firstOption.options[0]?.value
        : firstOption.value;

      if (firstValue) {
        setFieldValue(name, firstValue);
      }
    }
  }, [name, options, currentValue, setFieldValue, multiple]);

  return (
    <div>
      <LabelWithTooltip
        forId={id}
        label={label}
        tooltipText={
          (tooltipText ? tooltipText + " " : "") +
          (multiple
            ? "Select multiple by holding down the Ctrl key (Cmd on Mac)."
            : "")
        }
        tooltipLink={tooltipLink}
        required={required}
      />
      <Field
        as="select"
        id={id}
        name={name}
        required={required}
        multiple={multiple}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50"
      >
        {options.map((optionOrGroup) =>
          isOptionGroup(optionOrGroup) ? (
            <optgroup key={optionOrGroup.label} label={optionOrGroup.label}>
              {optionOrGroup.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={optionOrGroup.value} value={optionOrGroup.value}>
              {optionOrGroup.label}
            </option>
          ),
        )}
      </Field>
      <ErrorMessage
        name={name}
        component="div"
        className="text-red-500 text-sm mt-1"
      />
    </div>
  );
}
